import { Component, Input } from '@angular/core';

/**
 * Defines a segment type (category) for the stacked bar chart.
 * Each segment type represents a status or category with its own color.
 */
export interface StackedBarSegmentType {
  /** Unique identifier for this segment type */
  key: string;
  /** Translation key for the label */
  label: string;
  /** CSS class name for styling (e.g., 'segment-in-progress') */
  cssClass: string;
}

export const SEGMENT_TYPE_IN_PROGRESS: StackedBarSegmentType = {
  key: 'inProgress',
  label: 'inProgress',
  cssClass: 'segment-in-progress',
};

export const SEGMENT_TYPE_DUE_SOON: StackedBarSegmentType = {
  key: 'dueSoon',
  label: 'dueSoon',
  cssClass: 'segment-due-soon',
};

export const SEGMENT_TYPE_EXPIRED: StackedBarSegmentType = {
  key: 'expired',
  label: 'expired',
  cssClass: 'segment-expired',
};

export const SEGMENT_TYPE_CRITICAL: StackedBarSegmentType = {
  key: 'critical',
  label: 'critical',
  cssClass: 'segment-critical',
};

/**
 * Row data representing a single entity (e.g., a vessel).
 * Each row displays a stacked bar with segments.
 */
export interface StackedBarRowData {
  /** Row label (e.g., vessel name) */
  name: string;
  /** Values for each segment type, keyed by StackedBarSegmentType.key */
  values: Record<string, number>;
}

@Component({
  selector: 'app-ui-stacked-bar-chart',
  templateUrl: './ui-stacked-bar-chart.component.html',
  styleUrls: ['./ui-stacked-bar-chart.component.scss'],
  standalone: false,
})
export class UiStackedBarChartComponent {
  @Input() segmentTypes: StackedBarSegmentType[] = [];
  @Input() rows: StackedBarRowData[] = [];
  @Input() showLegend = true;
  @Input() showBadges = true;
  @Input() rowLabelHeader = '';
  @Input() valuesHeader = '';

  getRowTotal(row: StackedBarRowData): number {
    return this.segmentTypes.reduce((sum, segment) => sum + (row.values[segment.key] || 0), 0);
  }

  get maxTotal(): number {
    return Math.max(...this.rows.map((row) => this.getRowTotal(row)), 1);
  }

  getSegmentWidth(row: StackedBarRowData, segmentKey: string): number {
    const value = row.values[segmentKey] || 0;
    const total = this.getRowTotal(row);
    if (total === 0) {
      return 0;
    }
    // Scale relative to maxTotal so bars are comparable
    return (value / this.maxTotal) * 100;
  }

  getSegmentValue(row: StackedBarRowData, segmentKey: string): number {
    return row.values[segmentKey] || 0;
  }

  hasSegmentValue(row: StackedBarRowData, segmentKey: string): boolean {
    return (row.values[segmentKey] || 0) > 0;
  }
}
